import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Lock, UserPlus } from "lucide-react";

const TradeLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [regData, setRegData] = useState({ username: "", password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    try {
      const payload = new FormData();
      payload.append("username", formData.username);
      payload.append("password", formData.password);

      const response = await axios.post(`${BACKEND_URL}/api/auth/token`, payload);
      localStorage.setItem("trade_token", response.data.access_token);
      toast.success("Welcome back", { description: "Logged in successfully." });
      navigate("/trade/dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Login Failed", { description: "Invalid credentials or account pending approval." });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regData.password !== regData.confirmPassword) {
        toast.error("Passwords do not match");
        return;
    }
    setLoading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    try {
      await axios.post(`${BACKEND_URL}/api/auth/request-access`, {
          username: regData.username,
          password: regData.password
      });
      toast.success("Request Sent", { description: "Your account is pending approval by an administrator." });
      setIsLogin(true);
      setRegData({ username: "", password: "", confirmPassword: "" });
    } catch (error) {
      console.error(error);
      toast.error("Request Failed", { description: "Username may be taken." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dreibach-black flex items-center justify-center px-6">
      <Card className="w-full max-w-md bg-dreibach-card border-white/10">
        <CardHeader className="text-center space-y-4 pt-10">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10">
             {isLogin ? <Lock className="w-8 h-8 text-dreibach-teal" /> : <UserPlus className="w-8 h-8 text-dreibach-teal" />}
          </div>
          <CardTitle className="text-2xl font-black text-white uppercase tracking-widest">
              {isLogin ? "Trade Portal" : "Request Access"}
          </CardTitle>
          <p className="text-sm text-gray-400">
              {isLogin ? "Restricted Access. Authorised Personnel Only." : "Create an account for administrative approval."}
          </p>
        </CardHeader>
        <CardContent className="pb-10 px-8">
          {isLogin ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Username</label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 text-white focus:border-dreibach-teal h-12 rounded-none"
                    placeholder="Enter username"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 text-white focus:border-dreibach-teal h-12 rounded-none"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-dreibach-teal hover:bg-white hover:text-black text-white font-bold uppercase tracking-widest h-12 rounded-none transition-all mt-4"
                >
                  {loading ? "Authenticating..." : "Login"}
                </Button>
                <div className="text-center pt-4">
                    <button type="button" onClick={() => setIsLogin(false)} className="text-xs text-dreibach-teal hover:text-white uppercase tracking-wider font-bold">
                        Request New Account
                    </button>
                </div>
              </form>
          ) : (
              <form onSubmit={handleRegister} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Desired Username</label>
                  <Input
                    value={regData.username}
                    onChange={(e) => setRegData({...regData, username: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 text-white focus:border-dreibach-teal h-12 rounded-none"
                    placeholder="john.doe"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Password</label>
                  <Input
                    type="password"
                    value={regData.password}
                    onChange={(e) => setRegData({...regData, password: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 text-white focus:border-dreibach-teal h-12 rounded-none"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Confirm Password</label>
                  <Input
                    type="password"
                    value={regData.confirmPassword}
                    onChange={(e) => setRegData({...regData, confirmPassword: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 text-white focus:border-dreibach-teal h-12 rounded-none"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-dreibach-teal hover:text-white font-bold uppercase tracking-widest h-12 rounded-none transition-all mt-4"
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>
                <div className="text-center pt-4">
                    <button type="button" onClick={() => setIsLogin(true)} className="text-xs text-gray-500 hover:text-white uppercase tracking-wider font-bold">
                        Back to Login
                    </button>
                </div>
              </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradeLogin;
